/**
 * User service
 */
import { Deserialize, IJsonObject, Serialize } from 'dcerialize';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  RegisterUserList,
  User,
  UserDataToCheckPassword,
  UserImpersonateToken,
  UserList,
  UserPassword,
  UserPasswordAndToken,
  UserPasswordScore
} from '../models/user';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { CustomSnackbarService } from './custom-snackbar.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  /**
   * Constructor
   * @param http HTTP service
   * @param snackbarService - Custom snack bar service to show http errors
   * @param apiService - Api service to generate the path
   */
  constructor(
    protected http: HttpClient,
    private snackbarService: CustomSnackbarService,
    private apiService: ApiService
  ) {}

  get path(): string {
    return this.apiService.getApiUrl() + 'user';
  }

  /**
   * POST user
   * @param objectData - user or users to invite
   * @returns Results of the invitation as an observable
   */
  add(objectData: RegisterUserList): Observable<IJsonObject> {
    return this.http
      .post<IJsonObject>(
        `${this.path}/register`,
        Serialize(objectData, () => RegisterUserList)
      )
      .pipe(
        catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
      );
  }

  /**
   * Set new roles to a user
   * @param userData - user to change the roles
   * @returns void
   */
  changeRoles(userData: User): Observable<void> {

    return this.http.post<void>(this.path + '/set-roles', Serialize(userData, () => User))
      .pipe(
        catchError((err: HttpErrorResponse) => this.snackbarService.showError(err)));
  }

  /**
   * Get the current user
   * @returns element retrieved user-profile from the CRUD service
   */
  profile(): Observable<User> {
    return this.http.get<IJsonObject>(this.path + '/profile').pipe(
      map((user) => Deserialize(user, () => User)),
      catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
    );
  }

  /**
   * CRUD: UPDATE method
   * @param user - user to update
   */
  updateCurrentUser(user: User): Observable<User> {
    return this.http
      .put<IJsonObject>(
        `${this.path}/profile`,
        Serialize(user, () => User)
      )
      .pipe(
        map((newUser) => Deserialize(newUser, () => User)),
        catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
      );
  }

  /**
   * DELETE user
   */
  deleteProfile(): Observable<null> {
    return this.http.delete<null>(`${this.path}/profile`).pipe(
      map(() => null),
      catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
    );
  }

  /**
   * CRUD user-profile method
   * @returns element retrieved user-profile from the CRUD service
   */
  get(userId: number): Observable<User> {
    return this.http.get<IJsonObject>(`${this.path}/${userId}`).pipe(
      map((user) => Deserialize(user, () => User)),
      catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
    );
  }

  /**
   * CRUD user-profile method
   * @returns element retrieved user-profile from the CRUD service
   */
  list(params?: Record<string, string>): Observable<UserList> {
    return this.http.get<IJsonObject>(`${this.path}/list/deleted`, { params }).pipe(
      map((user) => Deserialize(user, () => UserList)),
      catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
    );
  }

  /**
   * CRUD user-profile method
   */
  delete(id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.path}/${id}`)
      .pipe(catchError((err: HttpErrorResponse) => this.snackbarService.showError(err)));
  }

  /**
   * Register current user in a course
   * @returns courseId course id
   */
  registerInCourse(courseId: number): Observable<void> {
    return this.http
      .post<void>(`${this.path}/course/${courseId}`, null)
      .pipe(catchError((err: HttpErrorResponse) => this.snackbarService.showError(err)));
  }

  /**
   * Method to activate an account
   *
   * @param token - string  activate account token
   */
  public activateAccount(token: string): Observable<any> {
    return this.http.post<null>(`${this.path}/activate-account/${token}`, null);
  }

  /**
   * Get Password Score
   *
   * @returns element retrieved user-profile from the CRUD service
   */
  getPasswordScore(userData: UserDataToCheckPassword): Observable<UserPasswordScore> {
    return this.http
      .patch<IJsonObject>(
        `${this.path}/password-score`,
        Serialize(userData, () => UserDataToCheckPassword)
      )
      .pipe(
        map((score) => Deserialize(score, () => UserPasswordScore)),
        catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
      );
  }

  /**
   * Get Password Score with token
   *
   * @returns element retrieved user-profile from the CRUD service
   */
  getPasswordScoreWithToken(passwordAndToken: UserPasswordAndToken): Observable<UserPasswordScore> {
    return this.http.patch<IJsonObject>(`${this.path}/password-score-with-token`, passwordAndToken).pipe(
      map((score) => Deserialize(score, () => UserPasswordScore)),
      catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
    );
  }

  /**
   * Get Password Score with token
   *
   * @returns element retrieved user-profile from the CRUD service
   */
  resetPasswordScoreWithToken(token: string, password: UserPassword): Observable<UserPasswordScore> {
    return this.http
      .patch<IJsonObject>(
        `${this.path}/reset-password/${token}`,
        Serialize(password, () => UserPassword)
      )
      .pipe(
        map((score) => Deserialize(score, () => UserPasswordScore)),
        catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
      );
  }
  /**
   * Resend restore password email
   *
   */
  resendRestorePassword(user: User): Observable<void> {
    return this.http
      .post<void>(`${this.path}/resend-mail`, { email: user.email })
      .pipe(catchError((err: HttpErrorResponse) => this.snackbarService.showError(err)));
  }

  reactivateUser(userEmail: string): Observable<void> {
    return this.http
      .post<void>(`${this.path}/reactivate`, { email: userEmail })
      .pipe(catchError((err: HttpErrorResponse) => this.snackbarService.showError(err)));
  }

  getImpersonateToken(email: string, logs: boolean): Observable<UserImpersonateToken> {
    return this.http
      .post<IJsonObject>(`${this.path}/impersonate-token`, {
        email: email, host: this.apiService.getApiHostname(), logs: logs
      })
      .pipe(
        map((token) => Deserialize(token, () => UserImpersonateToken)),
        catchError((err: HttpErrorResponse) => this.snackbarService.showError(err))
      );
  }
}
